import { LogoIcon } from '@repo/design-system/components/ui/v2/logo';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  groupNav,
  NAV,
  NAV_GROUP_LABEL,
  visibleNav,
  type NavGroup,
  type NavItem,
} from '../../lib/nav.ts';
import { useSession } from '../../lib/session.tsx';
import { cn } from '../../lib/utils.ts';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip.tsx';
import { OrgSwitcher, ProfileMenu, ThemeMenu } from './account.tsx';

/**
 * The sidebar, laid out as Leve's (TS-1681; Hriq `apps/app/.../components/sidebar.tsx`) in the
 * design system's v2 Sidebar tokens: a floating card; the logo; the organisation switcher; the first section (Workspace) in a grey rounded card where the
 * current page is a white row, the other sections under their labels; the theme toggle and the
 * profile row at the foot. Entries come from `NAV`, filtered by the session's permissions and
 * flags, so a page the API would refuse is not offered. On desktop the Shell shows it collapsed
 * (an icon rail) and expands it while hovered or keyboard-focused; the phone drawer is always full.
 *
 * Active matching: `/` and `/settings` match exactly, because every other entry under
 * `/settings/…` would otherwise light Settings up as well.
 */
export function Sidebar({
  collapsed,
  collapsedGroups = [],
  onToggleGroup,
}: {
  collapsed: boolean;
  collapsedGroups?: readonly string[];
  onToggleGroup?: (group: NavGroup) => void;
}) {
  const session = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const sections = groupNav(visibleNav(NAV, session));
  return (
    <aside
      className={cn(
        'ds-v2 flex h-full shrink-0 flex-col rounded-(--v2-sidebar-radius) border border-(--v2-sidebar-border) bg-(--v2-sidebar-bg) shadow-(--v2-sidebar-shadow) transition-[width] duration-200 ease-out',
        collapsed ? 'w-[68px]' : 'w-64',
      )}
      aria-label="Primary"
      data-collapsed={collapsed}
    >
      <div
        className={cn(
          'flex shrink-0 gap-2 border-b border-(--v2-sidebar-border)',
          collapsed ? 'flex-col items-center py-4' : 'h-[68px] items-center justify-between px-4',
        )}
      >
        <BrandLink collapsed={collapsed} />
      </div>
      {/* `*:shrink-0`: scroll when the entries outgrow the sidebar, instead of squeezing them. */}
      <nav
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-3 *:shrink-0',
          collapsed ? 'px-2' : 'px-3',
        )}
        aria-label="Main navigation"
      >
        <OrgSwitcher collapsed={collapsed} />
        {sections.map((section, i) =>
          i === 0 ? (
            // Leve's primary nav card: grey, rounded, the current page a white row.
            <div
              key={section.group}
              className="group/navcard flex flex-col gap-0.5 rounded-xl bg-(--v2-sidebar-row-active-bg) p-1.5"
              data-nav-card="true"
            >
              {section.items.map((item) => (
                <NavLink key={item.to} item={item} collapsed={collapsed} card />
              ))}
            </div>
          ) : (
            <NavSection
              key={section.group}
              group={section.group}
              items={section.items}
              collapsed={collapsed}
              current={section.items.some((item) => isActive(item.to, pathname))}
              open={collapsed || !collapsedGroups.includes(section.group)}
              onToggle={onToggleGroup && (() => onToggleGroup(section.group))}
            />
          ),
        )}
      </nav>
      <div className="shrink-0 border-t border-(--v2-sidebar-border) p-3">
        <div className={cn('mb-2 flex', collapsed ? 'justify-center' : 'px-1')}>
          <ThemeMenu />
        </div>
        <ProfileMenu collapsed={collapsed} />
      </div>
    </aside>
  );
}

export function BrandLink({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <Link to="/" className="flex min-w-0 items-center gap-2" aria-label="CRM home">
      <LogoIcon className="size-7 shrink-0" aria-hidden />
      {!collapsed && (
        <span className="truncate text-xl font-bold tracking-tight text-(--v2-sidebar-heading)">
          CRM
        </span>
      )}
    </Link>
  );
}

/** Must match `NavLink`'s `activeOptions`. */
function isActive(to: string, pathname: string) {
  if (to === '/' || to === '/settings') return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function NavSection({
  group,
  items,
  collapsed,
  current,
  open: unfolded,
  onToggle,
}: {
  group: NavGroup;
  items: NavItem[];
  collapsed: boolean;
  current: boolean;
  open: boolean;
  onToggle: (() => void) | undefined;
}) {
  const open = unfolded || current;
  return (
    <div className="flex flex-col gap-0.5" data-nav-group={group}>
      {collapsed ? (
        <div className="mx-2 mb-1 h-px bg-(--v2-sidebar-border)" aria-hidden />
      ) : (
        <button
          type="button"
          onClick={onToggle}
          disabled={current}
          aria-expanded={open}
          className="flex items-center justify-between rounded-md disabled:pointer-events-none px-2 pt-1 pb-1 text-[10px] font-semibold tracking-wider text-(--v2-sidebar-muted-foreground) uppercase transition-colors hover:text-(--v2-sidebar-text-primary) focus-visible:shadow-(--v2-sidebar-row-focus-shadow) focus-visible:outline-none"
        >
          {NAV_GROUP_LABEL[group]}
          <ChevronDown
            className={cn(
              'size-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
              !open && '-rotate-90',
            )}
            aria-hidden
          />
        </button>
      )}
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
        inert={!open}
        aria-hidden={!open}
      >
        <div className="-m-1 flex min-h-0 flex-col gap-0.5 overflow-hidden p-1">
          {items.map((item) => (
            <NavLink key={item.to} item={item} collapsed={collapsed} />
          ))}
        </div>
      </div>
    </div>
  );
}

function NavLink({
  item,
  collapsed,
  card = false,
}: {
  item: NavItem;
  collapsed: boolean;
  card?: boolean;
}) {
  const Icon = item.icon;
  const link: ReactNode = (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === '/' || item.to === '/settings' }}
      className={cn(
        'flex h-10 items-center gap-3 rounded-lg text-sm text-(--v2-sidebar-text-secondary) transition-colors focus-visible:shadow-(--v2-sidebar-row-focus-shadow) focus-visible:outline-none data-[status=active]:font-medium data-[status=active]:text-(--v2-sidebar-text-primary)',
        card
          ? 'hover:bg-(--v2-sidebar-bg) data-[status=active]:bg-(--v2-sidebar-bg) data-[status=active]:shadow-(--v2-sidebar-help-button-shadow)'
          : 'hover:bg-(--v2-sidebar-row-hover-bg) data-[status=active]:bg-(--v2-sidebar-row-active-bg)',
        collapsed ? 'justify-center' : 'px-2.5',
      )}
      data-nav={item.label}
    >
      <Icon className="size-[18px] shrink-0" aria-hidden />
      {collapsed ? (
        <span className="sr-only">{item.label}</span>
      ) : (
        <span className="truncate">{item.label}</span>
      )}
    </Link>
  );
  // The same tree expanded or not (the tooltip just stays shut when the labels are showing), so
  // the link is never replaced under a pointer that is about to click it as the rail expands.
  return (
    <Tooltip {...(collapsed ? {} : { open: false })}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}
